import React, { useState } from 'react';
import { ArrowLeft, Mail, MessageSquare, HelpCircle, ChevronDown, ChevronUp, Send, AlertCircle, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FAQ_ITEMS = [
  {
    question: "How do I add patients?",
    answer: "Use the 'Add Patient' button in the Patients tab. You can add patients individually by entering their email, or bulk upload using a CSV file. Patients will receive an invitation email to join your organization."
  },
  {
    question: "How do licenses work?",
    answer: "Each patient requires one active license. When you add a patient, a license is automatically assigned. You can manage your licenses in the Licenses tab, including purchasing additional licenses or reassigning unused ones."
  },
  {
    question: "Can I export patient data?",
    answer: "Yes! Use the Export option in the Analytics section. You can export compliance reports, patient progress data, and analytics in CSV or PDF format. You can also schedule automatic weekly or monthly reports."
  },
  {
    question: "How is the Compliance Score calculated?",
    answer: "The Compliance Score (0-100) is calculated based on: meal logging frequency (40%), carbohydrate target adherence (30%), glycemic index of meals (20%), and overall app engagement (10%). Higher scores indicate better diabetes management behaviors."
  },
  {
    question: "What is Estimated HbA1c?",
    answer: "Estimated HbA1c is calculated using the ADAG formula: (Average Glucose + 46.7) / 28.7. It requires at least 3 glucose readings in the past 30 days. This estimate should be used alongside lab HbA1c tests, not as a replacement."
  },
  {
    question: "How do I assign a dietitian to patients?",
    answer: "Go to Admin > Users, find the patient, and click 'Assign Dietitian'. Select the dietitian from the dropdown. The dietitian will then see the patient in their dashboard."
  },
  {
    question: "Can I customize the meal analysis categories?",
    answer: "Currently, PlateMind uses standard diabetes nutrition therapy categories. Custom categories will be available in a future update. Contact us if you have specific requirements for your organization."
  },
  {
    question: "How often are scheduled reports sent?",
    answer: "You can configure reports to be sent weekly (choose any day) or monthly (choose any date). Reports are sent at 8 AM UTC on the scheduled day. You can also trigger reports manually anytime."
  }
];

export const PartnerSupport = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    clinicName: '',
    issueType: 'technical',
    description: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // In production, this would send to backend
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    console.log('Support request submitted:', formData);
    setSubmitted(true);
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#FFF7F0]">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <button 
            onClick={() => navigate('/partner')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Partner Support</h1>
        <p className="text-gray-600 mb-8">Get help with your PlateMind partner account</p>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Options */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Us</h2>
              
              <div className="space-y-4">
                <a 
                  href="mailto:support@platemind.app"
                  className="flex items-center gap-3 p-4 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors"
                >
                  <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Email Support</p>
                    <p className="text-sm text-gray-500">support@platemind.app</p>
                  </div>
                </a>
                
                <a 
                  href="mailto:partnership@platemind.app"
                  className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
                >
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Partnership Inquiries</p>
                    <p className="text-sm text-gray-500">partnership@platemind.app</p>
                  </div>
                </a>
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <strong>Response Time:</strong> We typically respond within 24-48 business hours.
                </p>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white">
              <h3 className="font-semibold mb-2">Designed by Clinical Dietitians</h3>
              <p className="text-sm text-orange-100">
                PlateMind is built with input from healthcare professionals to ensure clinical accuracy and practical usability.
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* FAQ Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <HelpCircle className="w-6 h-6 text-orange-500" />
                <h2 className="text-xl font-semibold text-gray-900">Frequently Asked Questions</h2>
              </div>
              
              <div className="space-y-3">
                {FAQ_ITEMS.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-gray-900">{item.question}</span>
                      {openFaq === index ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    {openFaq === index && (
                      <div className="px-4 pb-4">
                        <p className="text-gray-600">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Report Issue Form */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <AlertCircle className="w-6 h-6 text-orange-500" />
                <h2 className="text-xl font-semibold text-gray-900">Report a Problem</h2>
              </div>
              
              {submitted ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-green-800 mb-2">Report Submitted!</h3>
                  <p className="text-green-700">Thank you for your feedback. Our support team will review your issue and respond within 24-48 hours.</p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({name: '', email: '', clinicName: '', issueType: 'technical', description: ''});
                    }}
                    className="mt-4 text-green-600 hover:text-green-700 font-medium"
                  >
                    Submit Another Report
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="john@clinic.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Clinic/Organization Name</label>
                      <input
                        type="text"
                        value={formData.clinicName}
                        onChange={(e) => setFormData({...formData, clinicName: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="ABC Medical Center"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Issue Type</label>
                      <select
                        value={formData.issueType}
                        onChange={(e) => setFormData({...formData, issueType: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="technical">Technical Issue</option>
                        <option value="billing">Billing Question</option>
                        <option value="feature">Feature Request</option>
                        <option value="data">Data/Report Issue</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Issue Description</label>
                    <textarea
                      required
                      rows={5}
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                      placeholder="Please describe the issue you're experiencing in detail..."
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>Processing...</>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Submit Report
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Footer Disclaimer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500">
            Need urgent help? Contact <a href="mailto:support@platemind.app" className="text-orange-500 hover:underline">support@platemind.app</a>
          </p>
          <p className="text-xs text-gray-400 mt-2">
            PlateMind provides nutrition guidance and does not replace professional medical advice.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PartnerSupport;
